import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Bot, Loader2, Save, Plus, X, Database, Trash2, Pencil, MessageSquare, Zap,
  PlayCircle, Users,
} from 'lucide-react'
import {
  getAssistantConfig, saveAssistantConfig, listBusinessAreas,
  saveBusinessArea, deleteBusinessArea, type BusinessAreaSummary,
} from '@/api/designers'
import { getLlmBundle } from '@/api/settings'
import type { LlmConfig } from '@/types'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useFunctionalRoles } from '@/components/common/PolicyPickers'
import BusinessAreaDesignerPage from './BusinessAreaDesignerPage'
import AssistantIntentsTab from './AssistantIntentsTab'
import { AssistantPreviewModal, ProfileCheckboxes, useProfileChoices } from './AssistantAccessControls'

/** Who may use the Assistant / one Business Area. `mode: 'all'` (assistant) and
 *  a missing entry (area) both mean "no extra restriction". */
export interface AccessRule {
  mode?: 'all' | 'roles' | 'inherit'
  allow_roles?: string[]
  deny_roles?: string[]
}

interface AssistantCfg {
  enabled: boolean
  display_name: string
  greeting: string
  ai_profile: string
  engine: string
  capabilities: { query?: boolean; visualize?: boolean; documents?: boolean; act?: boolean; import?: boolean; delegate?: boolean; intents?: boolean }
  allow_raw_sql: boolean
  access: AccessRule
  business_areas: string[]
  business_area_access: Record<string, AccessRule>
  suggested_questions: string[]
  limits: { max_rows?: number; max_upload_mb?: number }
}

/**
 * The capability toggles, in the registry's order. `delegate` is the one that
 * puts work in OTHER people's lists, which is why it is off until an owner
 * turns it on — and why its label says "ask" rather than "do": the Assistant
 * creates the task, the assignee does the work under their own rights.
 */
const CAPABILITIES: Array<{
  key: 'query' | 'visualize' | 'documents' | 'act' | 'import' | 'delegate' | 'intents'
  label: string
  hint?: string
}> = [
  { key: 'query', label: 'Query data' },
  { key: 'visualize', label: 'Auto-charts' },
  {
    key: 'documents',
    label: 'Answer from documents',
    hint: 'Answer from the content of controlled documents. Needs a Business Area with document sources AND question answering enabled on the repository (Document Designer).',
  },
  { key: 'act', label: 'Change data (run actions)' },
  {
    key: 'import',
    label: 'Import data files',
    hint: 'Let users attach a CSV / Excel / JSON file in the chat. The Assistant maps its columns (child forms included), cleanses the values against the screen\u2019s model, flags inconsistencies, and opens a side-by-side spreadsheet preview where the user corrects and saves. Follows "Change data" until set here.',
  },
  {
    key: 'delegate',
    label: 'Ask someone to do something',
    hint: 'Let the Assistant put a task in a person\u2019s or a role\u2019s work list, carrying the data the user described. It creates the TASK, never the record \u2014 the assignee opens it and saves under their own rights.',
  },
  { key: 'intents', label: 'Defined intents' },
]

const DEFAULTS: AssistantCfg = {
  enabled: false, display_name: 'Assistant', greeting: 'Hi! Ask me about your data.',
  ai_profile: '', engine: '', capabilities: { query: true, visualize: true, documents: true, act: false, delegate: false, intents: true },
  allow_raw_sql: false, access: { mode: 'all', allow_roles: [], deny_roles: [] },
  business_areas: [], business_area_access: {},
  suggested_questions: [], limits: { max_rows: 1000, max_upload_mb: 10 },
}

/** One-line description of what a saved AI profile actually runs, so the
 *  designer picks by capability rather than by remembering a name. */
function profileLabel(c: LlmConfig): string {
  if (c.mode === 'agent') {
    const agent = c.agent === 'claude_code' ? 'Claude Code agent'
      : c.agent === 'codex' ? 'Codex agent'
        : c.agent === 'opencode' ? 'OpenCode agent' : `${c.agent} agent`
    const auth = c.auth === 'subscription' ? 'subscription'
      : c.auth === 'configured' ? 'OpenCode providers' : 'API key'
    return `${agent} · ${auth}${c.model ? ` · ${c.model}` : ''}`
  }
  return `Built-in LLM · ${c.provider}${c.model ? ` · ${c.model}` : ''}`
}

const inp: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 15, background: 'var(--color-bg)', color: 'var(--color-text)', outline: 'none',
}
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }

type Tab = 'assistant' | 'areas' | 'intents'

// ── Configure AI page ───────────────────────────────────────────────
// Project-level home for the end-user AI Assistant. Reached from the
// workbench Advanced tab ("Configure AI" card) — NOT from navigation.
// Two tabs: the Assistant settings, and the Business Areas the Assistant
// queries (curated datasets, managed here rather than as nav objects).
export default function AssistantConfigPage() {
  const { project: projectParam } = useParams<{ project: string }>()
  const project = projectParam || ''
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('assistant')
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const readOnly = !canEdit

  return (
    <div style={{ padding: 20, maxWidth: 980, margin: '0 auto', color: 'var(--color-text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button onClick={() => navigate(-1)} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><ArrowLeft size={15} /> Back</button>
        <Bot size={20} style={{ color: 'var(--color-accent)' }} />
        <h2 style={{ margin: 0, fontSize: 19 }}>Configure AI</h2>
        {readOnly && <ReadOnlyBadge />}
      </div>

      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginTop: 0, marginBottom: 14 }}>
        Set up the end-user AI Assistant inside your deployed app — it answers questions over your Business Areas,
        draws charts, and (optionally) changes data by running actions the user is authorized for (it proposes; the user confirms).
        The chatbot block on a smart page uses the same assistant.
      </p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: 16 }}>
        {([['assistant', 'Assistant', Bot], ['areas', 'Business Areas', Database], ['intents', 'Intents', Zap]] as [Tab, string, typeof Bot][]).map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 15, fontWeight: tab === id ? 700 : 500,
            color: tab === id ? 'var(--color-accent)' : 'var(--color-text-secondary)',
            borderBottom: tab === id ? '2px solid var(--color-accent)' : '2px solid transparent',
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Icon size={15} /> {label}</button>
        ))}
      </div>

      {tab === 'assistant' && <AssistantSettings project={project} readOnly={readOnly} />}
      {tab === 'areas' && <BusinessAreasManager project={project} readOnly={readOnly} />}
      {tab === 'intents' && <AssistantIntentsTab project={project} readOnly={readOnly} />}
    </div>
  )
}

// ── Assistant settings tab ──────────────────────────────────────────
function AssistantSettings({ project, readOnly }: { project: string; readOnly: boolean }) {
  const [cfg, setCfg] = useState<AssistantCfg>(DEFAULTS)
  const [areas, setAreas] = useState<BusinessAreaSummary[]>([])
  const [profiles, setProfiles] = useState<LlmConfig[]>([])
  const [defaultProfile, setDefaultProfile] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const funcRoles = useFunctionalRoles(project)

  useEffect(() => {
    if (!project) return
    setLoading(true)
    Promise.all([getAssistantConfig(project), listBusinessAreas(project)])
      .then(([c, a]) => {
        const incoming = (c.config?.assistant as Partial<AssistantCfg>) || {}
        const caps: Record<string, boolean> = { ...((incoming.capabilities as Record<string, boolean>) || {}) }
        // Legacy: the old "create" (direct insert) capability migrates to "act".
        if (caps.create && caps.act === undefined) caps.act = true
        delete caps.create
        setCfg({
          ...DEFAULTS, ...incoming,
          capabilities: { ...DEFAULTS.capabilities, ...caps },
          access: { ...DEFAULTS.access, ...(incoming.access || {}) },
          business_area_access: { ...(incoming.business_area_access || {}) },
        })
        setAreas(a.business_areas || [])
      })
      .catch(() => toast.error('Failed to load assistant config'))
      .finally(() => setLoading(false))
  }, [project])

  // The saved AI profiles (Settings → AI) that can serve assistant queries —
  // API-key profiles run the built-in LLM engine, agent profiles run Claude
  // Code / Codex. Listing them beats asking the designer to retype a name.
  useEffect(() => {
    getLlmBundle()
      .then((b) => { setProfiles(b.configs || []); setDefaultProfile(b.default_config_name || '') })
      .catch(() => { /* the free-text fallback below still works */ })
  }, [])

  const set = useCallback(<K extends keyof AssistantCfg>(k: K, v: AssistantCfg[K]) =>
    setCfg((c) => ({ ...c, [k]: v })), [])

  const save = async () => {
    setSaving(true)
    try {
      await saveAssistantConfig(project, { assistant: cfg as unknown as Record<string, unknown> })
      toast.success('Assistant config saved')
    } catch { toast.error('Failed to save') } finally { setSaving(false) }
  }

  // Every profile named anywhere in this config — so a role dropped from the
  // catalogue is still shown (and removable) instead of vanishing silently.
  const referencedRoles = useMemo(() => {
    const out = [...(cfg.access.allow_roles || []), ...(cfg.access.deny_roles || [])]
    for (const rule of Object.values(cfg.business_area_access || {})) {
      out.push(...(rule.allow_roles || []), ...(rule.deny_roles || []))
    }
    return out
  }, [cfg.access, cfg.business_area_access])
  const profileChoices = useProfileChoices(funcRoles, referencedRoles)
  // Restricted mode narrows the per-area lists to the profiles that hold the
  // Assistant at all — granting an area to a profile that can't open the
  // Assistant would be a rule that never fires.
  const grantedProfiles = cfg.access.mode === 'roles'
    ? (cfg.access.allow_roles || []) : profileChoices

  const setAccess = (patch: Partial<AccessRule>) => set('access', { ...cfg.access, ...patch })
  const areaRule = (name: string): AccessRule => cfg.business_area_access?.[name] || { mode: 'inherit' }
  const setAreaRule = (name: string, rule: AccessRule) =>
    set('business_area_access', { ...cfg.business_area_access, [name]: rule })

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}><Loader2 className="animate-spin" /></div>

  const toggleArea = (name: string) => set('business_areas',
    cfg.business_areas.includes(name) ? cfg.business_areas.filter((a) => a !== name) : [...cfg.business_areas, name])
  // With no explicit list every published area is offered, so the per-area
  // profile rules must be configurable for all of them, not only the ticked ones.
  const scopedAreas = cfg.business_areas.length
    ? areas.filter((a) => cfg.business_areas.includes(a.area_name)) : areas

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Assistant settings</h3>
        <button onClick={() => setPreviewing(true)} disabled={!cfg.enabled}
          title={cfg.enabled ? 'Try the saved configuration, optionally as one of the granted profiles' : 'Enable the Assistant first'}
          style={{ ...inp, marginLeft: 'auto', cursor: cfg.enabled ? 'pointer' : 'not-allowed', opacity: cfg.enabled ? 1 : 0.55, display: 'flex', alignItems: 'center', gap: 5 }}>
          <PlayCircle size={15} /> Preview
        </button>
        {!readOnly && (
          <button onClick={save} disabled={saving} style={{ ...inp, cursor: 'pointer', background: 'var(--color-accent)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
        )}
      </div>

      <AssistantPreviewModal
        project={project} open={previewing} onClose={() => setPreviewing(false)}
        profiles={grantedProfiles} displayName={cfg.display_name}
      />

      <div style={{ display: 'grid', gap: 16 }}>
        <label style={row}><input type="checkbox" checked={cfg.enabled} disabled={readOnly} onChange={(e) => set('enabled', e.target.checked)} /> <b>Enable the Assistant</b> in this app</label>

        <Field label="Display name"><input style={inp} value={cfg.display_name} disabled={readOnly} onChange={(e) => set('display_name', e.target.value)} /></Field>
        <Field label="Greeting"><input style={inp} value={cfg.greeting} disabled={readOnly} onChange={(e) => set('greeting', e.target.value)} /></Field>

        {/* ── Who can use the Assistant ─────────────────────────────── */}
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 14, display: 'grid', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Users size={15} style={{ color: 'var(--color-accent)' }} />
            <strong style={{ fontSize: 15 }}>Who can use the Assistant</strong>
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: -6 }}>
            Users whose profile isn't granted never see the Assistant — the launcher icon doesn't render for them,
            and a direct request is refused.
          </div>
          <label style={row}>
            <input type="radio" name="assistant-access" checked={cfg.access.mode !== 'roles'} disabled={readOnly}
              onChange={() => setAccess({ mode: 'all' })} />
            All users of the app
          </label>
          <label style={row}>
            <input type="radio" name="assistant-access" checked={cfg.access.mode === 'roles'} disabled={readOnly}
              onChange={() => setAccess({ mode: 'roles' })} />
            Only these profiles
          </label>
          {cfg.access.mode === 'roles' && (
            <div style={{ paddingLeft: 22, display: 'grid', gap: 8 }}>
              <ProfileCheckboxes choices={profileChoices} value={cfg.access.allow_roles || []}
                disabled={readOnly} onChange={(next) => setAccess({ allow_roles: next })} />
              {!(cfg.access.allow_roles || []).length && (
                <div style={{ fontSize: 13, color: 'var(--color-warning, #b45309)' }}>
                  No profile selected — nobody but a runtime administrator will see the Assistant.
                </div>
              )}
            </div>
          )}
        </div>

        <Field label="AI profile" hint="Which saved AI profile (Settings → AI) serves assistant queries. Blank = your default profile. Built-in LLM, Claude Code and Codex profiles are all supported.">
          <select style={inp} value={cfg.ai_profile} disabled={readOnly} onChange={(e) => set('ai_profile', e.target.value)}>
            <option value="">{defaultProfile ? `Default profile (${defaultProfile})` : '(default profile)'}</option>
            {profiles.map((p) => (
              <option key={p.name} value={p.name}>{p.name} — {profileLabel(p)}</option>
            ))}
            {/* A profile saved earlier that no longer exists in the bundle stays
                selectable so saving doesn't silently reassign it. */}
            {cfg.ai_profile && !profiles.some((p) => p.name === cfg.ai_profile) && (
              <option value={cfg.ai_profile}>{cfg.ai_profile} — not found in Settings → AI</option>
            )}
          </select>
        </Field>

        <Field label="Engine" hint="How the Assistant runs. Auto follows the AI profile (API-key → built-in LLM; a Claude Code / Codex profile → that agent). Override only to force a specific engine.">
          <select style={inp} value={cfg.engine} disabled={readOnly} onChange={(e) => set('engine', e.target.value)}>
            <option value="">Auto (from AI profile)</option>
            <option value="llm">Built-in LLM</option>
            <option value="claude_code">Claude Code agent</option>
            <option value="codex">Codex agent</option>
          </select>
        </Field>

        <Field label="Capabilities">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {CAPABILITIES.map(({ key, label, hint }) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15 }}
                     title={hint}>
                <input type="checkbox" disabled={readOnly}
                  // `import` follows `act` until it is set explicitly — the
                  // server reads an absent key the same way.
                  checked={!!(key === 'import' ? (cfg.capabilities.import ?? cfg.capabilities.act) : cfg.capabilities[key])}
                  onChange={(e) => set('capabilities', { ...cfg.capabilities, [key]: e.target.checked })} />
                {label}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Business Areas the Assistant can use" hint="Leave all unchecked to allow every published Business Area the caller's role can access. Define areas in the Business Areas tab.">
          {areas.length === 0 ? <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>No Business Areas yet — create one in the Business Areas tab.</span> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {areas.map((a) => (
                <label key={a.area_name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: 8 }}>
                  <input type="checkbox" checked={cfg.business_areas.includes(a.area_name)} disabled={readOnly} onChange={() => toggleArea(a.area_name)} />
                  {a.area_name}
                </label>
              ))}
            </div>
          )}
        </Field>

        {/* ── Per-Business-Area profiles ────────────────────────────── */}
        {scopedAreas.length > 0 && (
          <Field
            label="Which profiles get which Business Area"
            hint="Of the profiles granted the Assistant above, choose who may be served information from each area. An area left on 'Everyone granted' follows the Assistant-level grant. The Assistant is never told an area exists that the asker may not use, so it cannot answer from it."
          >
            <div style={{ display: 'grid', gap: 8 }}>
              {scopedAreas.map((a) => {
                const rule = areaRule(a.area_name)
                const restricted = rule.mode === 'roles'
                return (
                  <div key={a.area_name} style={{ border: '1px solid var(--color-border)', borderRadius: 9, padding: 10, display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Database size={14} style={{ color: 'var(--color-accent)' }} />
                      <strong style={{ fontSize: 14 }}>{a.area_name}</strong>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        {a.allow_roles?.length ? `area roles: ${a.allow_roles.join(', ')}` : 'area open to any role'}
                      </span>
                      <select
                        style={{ ...inp, marginLeft: 'auto', padding: '4px 8px', fontSize: 13 }}
                        value={restricted ? 'roles' : 'inherit'} disabled={readOnly}
                        onChange={(e) => setAreaRule(a.area_name, e.target.value === 'roles'
                          ? { mode: 'roles', allow_roles: rule.allow_roles || [], deny_roles: rule.deny_roles || [] }
                          : { mode: 'inherit' })}
                      >
                        <option value="inherit">Everyone granted the Assistant</option>
                        <option value="roles">Only selected profiles</option>
                      </select>
                    </div>
                    {restricted && (
                      <>
                        <ProfileCheckboxes
                          choices={grantedProfiles} value={rule.allow_roles || []} disabled={readOnly}
                          emptyHint="Grant the Assistant to some profiles first."
                          onChange={(next) => setAreaRule(a.area_name, { ...rule, mode: 'roles', allow_roles: next })}
                        />
                        {!(rule.allow_roles || []).length && (
                          <div style={{ fontSize: 13, color: 'var(--color-warning, #b45309)' }}>
                            No profile selected — only a runtime administrator will be served this area.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </Field>
        )}

        <Field label="Suggested questions">
          {cfg.suggested_questions.map((q, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              <input style={{ ...inp, flex: 1 }} value={q} disabled={readOnly} onChange={(e) => set('suggested_questions', cfg.suggested_questions.map((x, j) => j === i ? e.target.value : x))} />
              {!readOnly && <button onClick={() => set('suggested_questions', cfg.suggested_questions.filter((_, j) => j !== i))} style={{ ...inp, cursor: 'pointer' }}><X size={13} /></button>}
            </div>
          ))}
          {!readOnly && <button onClick={() => set('suggested_questions', [...cfg.suggested_questions, ''])} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><Plus size={13} /> Add</button>}
        </Field>

        <label style={row}><input type="checkbox" checked={cfg.allow_raw_sql} disabled={readOnly} onChange={(e) => set('allow_raw_sql', e.target.checked)} /> Allow advanced raw-SQL queries <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>(higher risk; off by default)</span></label>

        <Field label="Limits" hint="Caps applied at runtime to protect your data and AI spend.">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 14 }}>
              Max rows per query
              <input type="number" min={1} style={{ ...inp, width: 150 }} value={cfg.limits.max_rows ?? 1000} disabled={readOnly}
                onChange={(e) => set('limits', { ...cfg.limits, max_rows: Math.max(1, Number(e.target.value) || 0) })} />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 14 }}>
              Max upload size (MB)
              <input type="number" min={1} style={{ ...inp, width: 150 }} value={cfg.limits.max_upload_mb ?? 10} disabled={readOnly}
                onChange={(e) => set('limits', { ...cfg.limits, max_upload_mb: Math.max(1, Number(e.target.value) || 0) })} />
            </label>
          </div>
        </Field>
      </div>
    </div>
  )
}

// ── Business Areas tab (list / create / edit / delete) ──────────────
function BusinessAreasManager({ project, readOnly }: { project: string; readOnly: boolean }) {
  const [areas, setAreas] = useState<BusinessAreaSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  const editorDirty = useRef(false)

  const refresh = useCallback(() => {
    if (!project) return
    setLoading(true)
    listBusinessAreas(project)
      .then((r) => setAreas(r.business_areas || []))
      .catch(() => setAreas([]))
      .finally(() => setLoading(false))
  }, [project])
  useEffect(() => { refresh() }, [refresh])

  const sanitizeName = (s: string) => s.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')

  const doCreate = async () => {
    const name = sanitizeName(newName)
    if (!name) { toast.error('Enter a valid name'); return }
    if (areas.some((a) => a.area_name.toUpperCase() === name)) { toast.error('A business area with that name already exists'); return }
    setBusy(true)
    try {
      const blank = {
        BUSINESS_AREA: {
          AREA_NAME: name, AREA_TYPE: 'A', DESCRIPTION: newName.trim().replace(/_/g, ' '), CONNECTION_ID: '',
          TABLES: [], JOINS: [], ROW_SECURITY: [], COLUMN_SECURITY: [],
          AI_INSTRUCTIONS: '', EXAMPLE_QUESTIONS: [], CREATE_TARGETS: [],
          ACCESS: { ALLOW_ROLES: [], DENY_ROLES: [] }, VISIBILITY: 'published',
        },
      }
      await saveBusinessArea(project, name, blank)
      toast.success('Business area created')
      setCreating(false); setNewName('')
      refresh()
      setEditing(name)
    } catch { toast.error('Failed to create business area') } finally { setBusy(false) }
  }

  const doDelete = async (name: string) => {
    if (!window.confirm(`Delete business area "${name}"? This cannot be undone.`)) return
    try { await deleteBusinessArea(project, name); toast.success(`Deleted ${name}`); refresh() }
    catch (err) {
      // The backend refuses (409) when a visual / assistant / menu still uses
      // this area — surface that message instead of a generic failure.
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const msg = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string')
          ? (detail as { message: string }).message
          : 'Failed to delete'
      toast.error(msg)
    }
  }

  const backToList = () => {
    if (editorDirty.current && !window.confirm('You have unsaved changes in this Business Area. Leave without saving?')) return
    editorDirty.current = false
    setEditing(null)
    refresh()
  }

  // Editor view — the full Business Area designer, embedded. It carries its
  // own Save / Generate-with-AI buttons; we just add a "back to list" affordance
  // and guard navigating away while dirty.
  if (editing) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={backToList} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><ArrowLeft size={15} /> Business Areas</button>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Use the editor's <b>Save</b> button to persist your changes.</span>
        </div>
        <BusinessAreaDesignerPage
          embedded embedProject={project} embedObjName={editing}
          onDirtyChange={(d) => { editorDirty.current = d }}
        />
      </div>
    )
  }

  // List view
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-secondary)', flex: 1 }}>
          Business Areas are curated datasets — tables, joins, descriptions and row/column security — that let the
          Assistant turn a plain-language question into safe, scoped SQL. They are configured here, not in navigation.
        </p>
        {!readOnly && (
          <button onClick={() => { setCreating(true); setNewName('') }} style={{ ...inp, cursor: 'pointer', background: 'var(--color-accent)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
            <Plus size={15} /> New Business Area
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-secondary)' }}><Loader2 className="animate-spin" /></div>
      ) : areas.length === 0 ? (
        <div style={{ padding: 36, textAlign: 'center', color: 'var(--color-text-secondary)', border: '1px dashed var(--color-border)', borderRadius: 10 }}>
          <MessageSquare size={26} style={{ opacity: 0.5 }} />
          <div style={{ marginTop: 8, fontSize: 15 }}>No Business Areas yet.</div>
          <div style={{ fontSize: 13 }}>Create one to let the Assistant query and chart your data.</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {areas.map((a) => (
            <div key={a.area_name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid var(--color-border)', borderRadius: 10 }}>
              <Database size={18} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong style={{ fontSize: 15 }}>{a.area_name}</strong>
                  <span style={{ fontSize: 13, padding: '1px 7px', borderRadius: 999, background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                    {a.visibility === 'draft' ? 'Draft' : 'Published'}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.description || '—'} · {a.table_count} table{a.table_count === 1 ? '' : 's'}
                  {a.allow_roles?.length ? ` · roles: ${a.allow_roles.join(', ')}` : ''}
                </div>
              </div>
              <button onClick={() => setEditing(a.area_name)} style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Pencil size={14} /> {readOnly ? 'View' : 'Edit'}
              </button>
              {!readOnly && (
                <button onClick={() => doDelete(a.area_name)} title="Delete" style={{ ...inp, cursor: 'pointer', color: 'var(--color-error, #dc2626)' }}><Trash2 size={14} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div onClick={() => setCreating(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 18, width: 'min(440px, 92vw)', color: 'var(--color-text)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
              <strong style={{ fontSize: 16 }}>New Business Area</strong>
              <button onClick={() => setCreating(false)} style={{ ...inp, cursor: 'pointer', marginLeft: 'auto', padding: 6 }}><X size={15} /></button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 0 }}>A short identifier (letters, numbers, underscores). You'll add tables and AI instructions next.</p>
            <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doCreate() }}
              placeholder="e.g. SALES_INSIGHTS" style={{ ...inp, width: '100%' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button onClick={() => setCreating(false)} style={{ ...inp, cursor: 'pointer' }}>Cancel</button>
              <button onClick={doCreate} disabled={busy || !newName.trim()} style={{ ...inp, cursor: 'pointer', background: 'var(--color-accent)', color: '#fff', border: 'none', display: 'flex', alignItems: 'center', gap: 5 }}>
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
      {hint && <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{hint}</div>}
      {children}
    </div>
  )
}
